import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { GoalVisibility, ReflectionStatus, StatusMeta } from "./types";
import {
  faGlobe,
  faLock,
  faUserPlus,
  faUsers,
} from "@fortawesome/pro-light-svg-icons";

export const GOAL_DRAWER_WIDTH = "w-[540px]";

export const VISIBILITY_ICONS: Record<GoalVisibility, IconProp> = {
  private: faLock,
  standard: faUsers,
  shared: faGlobe,
  custom: faUserPlus,
};

export const VISIBILITY_COLORS: Record<
  GoalVisibility,
  {
    textColor: string;
    borderColor: string;
    iconColor: string;
    backgroundColor: string;
  }
> = {
  private: {
    textColor: "#53565A",
    borderColor: "#DDDDDE",
    iconColor: "#75787B",
    backgroundColor: "#F7F7F7",
  },
  standard: {
    textColor: "#00445A",
    borderColor: "#B2E5F5",
    iconColor: "#00A9E0",
    backgroundColor: "#E5F6FB",
  },
  shared: {
    textColor: "#004D29",
    borderColor: "#B3ECD1",
    iconColor: "#00C067",
    backgroundColor: "#E5F9F0",
  },
  custom: {
    textColor: "#3B2D80",
    borderColor: "#C9BEFE",
    iconColor: "#7C5CFC",
    backgroundColor: "#F0EDFE",
  },
};

export const VISIBILITY_OPTIONS_MAP = {
  private: {
    icon: VISIBILITY_ICONS.private,
    title: "Private",
    desc: "Only I can see this",
  },
  standard: {
    icon: VISIBILITY_ICONS.standard,
    title: "Standard",
    desc: "Me + my manager + admins",
  },
  custom: {
    icon: VISIBILITY_ICONS.custom,
    title: "Custom",
    desc: "Visible to you, your manager, and admins.",
  },
  shared: {
    icon: VISIBILITY_ICONS.shared,
    title: "Shared",
    desc: "Everyone in my organization",
  },
} as Record<
  GoalVisibility,
  {
    icon: IconProp;
    title: string;
    desc: string;
  }
>;

export const VISIBILITY_TEXT: Record<GoalVisibility, string> = {
  private: "Only you can see this goal.",
  standard: "Visible to you, your manager, and admins.",
  shared: "Visible to everyone in your organization.",
  custom: "Visible to custom list of users.",
};

export const REFLECTION_STATUS_MAP: Record<ReflectionStatus, StatusMeta> = {
  on_track: {
    label: "On Track",
    bg: "var(--status-success-bg)",
    text: "var(--status-success-text)",
    statusDotColor: "var(--status-success)",
  },
  some_challenges: {
    label: "Some Challenges",
    bg: "var(--status-warning-bg)",
    text: "var(--status-warning-text)",
    statusDotColor: "var(--status-warning)",
  },
  unlikely_to_reach: {
    label: "Unlikely To Reach",
    bg: "var(--status-error-bg)",
    text: "var(--status-error-text)",
    statusDotColor: "var(--status-error)",
  },
};

export const GOAL_FILTER_LABEL_MAP = {
  all: "All",
  active: "Active",
  past: "Past",
  work: "Work",
  personal: "Personal",
  team: "Team",
  on_track: "On Track",
  some_challenges: "Some Challenges",
  unlikely_to_reach: "Unlikely To Reach",
};

export const GOAL_COACH_BO_PROMPTS: {
  [type: string]: {
    coachBotIntro: string;
    userPrompts: string[];
    type: "goals" | "orgGoals";
  };
} = {
  admin: {
    coachBotIntro:
      "I can help you build or reflect on your organization's work or teamwork goals using many frameworks from OKR's to SMART goals. Do you want help generating a goal or reflecting on one?",
    userPrompts: [
      "Set org-wide goals",
      "Review progress",
      "Align my leaders",
      "How do I roll this out",
      "Build accountability",
    ],
    type: "orgGoals",
  },
  user: {
    coachBotIntro:
      "I can help you build or reflect on your work, team, or personal goals using many frameworks from OKR's to SMART goals. Do you want help generating a goal or reflecting on one?",
    userPrompts: ["Set goals", "Reflect on goals"],
    type: "goals",
  },
};

export interface BestPracticeBox {
  type: "blue" | "gold";
  label: string;
  text: string;
}

export interface BestPracticeContent {
  paragraphs: string[];
  boxes: BestPracticeBox[];
}

export interface BestPracticeSection {
  id: string;
  icon: "clock" | "check-circle" | "edit" | "activity" | "trending-up" | "tool";
  title: string;
  subtitle: string;
  isDefaultExpanded: boolean;
  content: BestPracticeContent;
}

export interface BestPracticeData {
  title: string;
  sections: BestPracticeSection[];
}

export const GOAL_BEST_PRACTICE_DATA: BestPracticeData = {
  title: "Goal Setting Best Practices",
  sections: [
    {
      id: "why-goals-matter",
      icon: "clock",
      title: "Why goals matter",
      subtitle: "The research behind writing things down",
      isDefaultExpanded: true,
      content: {
        paragraphs: [
          "People who write down specific goals are significantly more likely to achieve them. Defining what success looks like creates accountability and focus.",
        ],
        boxes: [
          {
            type: "blue",
            label: "Matthews (2015):",
            text: "People who wrote down goals achieved 42% more than those who didn't. Sharing goals with an accountability partner increased achievement even further.",
          },
          {
            type: "blue",
            label: "Locke & Latham (1990, 2002):",
            text: 'Across 400+ studies, specific and challenging goals consistently outperform vague "do your best" goals, with effect sizes of d = 0.42–0.80.',
          },
        ],
      },
    },
    {
      id: "choose-category-type",
      icon: "check-circle",
      title: "Choose the right category & type",
      subtitle: "Two dimensions that unlock better coaching",
      isDefaultExpanded: false,
      content: {
        paragraphs: [
          'Develop captures two dimensions for every goal. Category is who the goal is for, and Type is what "done" looks like. Together they help Coach Bo give you better coaching.',
          'Categories: Work goals are tied to your role and business results (e.g. "Close $2M in Q3 revenue"). Team goals improve how your group collaborates (e.g. "Build trusted advisor relationship with VP of Sales"). Personal goals focus on your own growth (e.g. "Complete AWS Solutions Architect certification").',
          "Types: Hit a target (Outcome) means there's a number or milestone. Build a habit (Process) means success is the routine itself. Learn something (Learning) means you're building a new skill or knowledge area.",
        ],
        boxes: [
          {
            type: "blue",
            label: "Tip:",
            text: "A balanced portfolio drives better development. Pair outcome goals with at least one process or learning goal — outcomes are what you achieve, process and learning are how you grow.",
          },
        ],
      },
    },
    {
      id: "write-better-goals",
      icon: "edit",
      title: "Write better goals",
      subtitle: "What makes a goal specific enough to be useful",
      isDefaultExpanded: false,
      content: {
        paragraphs: [
          "Good goals are specific (someone else could evaluate it), measurable (you'll know when you hit it), challenging (stretches you beyond current performance), time-bound (has a deadline), and connected to a bigger purpose or team goal.",
        ],
        boxes: [
          {
            type: "blue",
            label: "Before → After:",
            text: '"Get better at presenting" becomes "Deliver 3 all-hands presentations with feedback score above 4/5 by Q3." "Be a better manager" becomes "Hold weekly 1:1s with all 5 reports for 12 consecutive weeks." "Learn SQL" becomes "Complete SQL cert and write 3 production queries independently."',
          },
        ],
      },
    },
    {
      id: "calibrate-difficulty",
      icon: "activity",
      title: "Calibrate difficulty",
      subtitle: "Standard, stretch, and moonshot goals",
      isDefaultExpanded: false,
      content: {
        paragraphs: [
          "The best goals are challenging but not impossible. Google targets 70% completion on their OKRs — hitting 100% means they were too easy. Standard goals (90-100% achievable) build confidence. Stretch goals (60-80% achievable) are the sweet spot for growth. Moonshot goals (30-50% achievable) are ambitious bets.",
        ],
        boxes: [
          {
            type: "blue",
            label: "Csikszentmihalyi (Flow research):",
            text: 'Optimal motivation occurs when challenge slightly exceeds current skill level — the "stretch zone" between comfort and overwhelm. Aim for 1-2 stretch goals, 1-2 standard goals for reliable wins, and maybe 1 moonshot if you\'re feeling ambitious.',
          },
        ],
      },
    },
    {
      id: "regular-reflection",
      icon: "trending-up",
      title: "Regular reflection drives results",
      subtitle: "Why check-ins matter and what a good one looks like",
      isDefaultExpanded: false,
      content: {
        paragraphs: [
          "Goals without check-ins fade into background noise. A good reflection takes about 2 minutes: update your progress percentage, pick a status (on track, some challenges, or unlikely to reach), and write one thing you'll do before your next reflection.",
        ],
        boxes: [
          {
            type: "blue",
            label: "Harkin et al. (2016), 138-study meta-analysis:",
            text: "Progress monitoring significantly increases the probability of reaching your goal. The effect is even stronger when monitoring is recorded and shared with someone.",
          },
          {
            type: "gold",
            label: "🤖 Bo tip:",
            text: "Set a monthly reflection reminder. Even a 2-minute update keeps goals alive and gives your manager signal on where to help.",
          },
        ],
      },
    },
    {
      id: "make-it-stick",
      icon: "tool",
      title: "Make it stick",
      subtitle: "Two research-backed techniques for follow-through",
      isDefaultExpanded: false,
      content: {
        paragraphs: [
          "The gap between setting a goal and making it happen is execution. Two techniques dramatically increase your odds: implementation intentions and public commitment.",
          'Implementation intentions ("If-then" plans): Instead of "I\'ll work on my goal," try "If it\'s Monday at 9am, then I\'ll spend 30 minutes on X." Research shows this simple reframing can double your success rate.',
          "Public commitment: Share your goal with your manager, a peer, or your team. Social accountability creates positive pressure and opens the door for support.",
        ],
        boxes: [
          {
            type: "blue",
            label: "Gollwitzer & Sheeran (2006), meta-analysis of 94 studies:",
            text: "Implementation intentions had a medium-to-large effect size (d = 0.65) on goal achievement across diverse populations and contexts.",
          },
        ],
      },
    },
  ],
};
